// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {VoiceDiagnosticsUploadError} from '@app/features/voice/diagnostics/VoiceDiagnosticsApi';
import {exportVoiceDiagnostics} from '@app/features/voice/utils/VoiceDiagnosticsExport';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useCallback, useState} from 'react';

const logger = new Logger('VoiceDiagnosticsSection');
export const VoiceDiagnosticsSection: React.FC = () => {
	const {t} = useLingui();
	const [isUploading, setIsUploading] = useState(false);
	const handleUpload = useCallback(async () => {
		setIsUploading(true);
		try {
			await exportVoiceDiagnostics();
			ToastCommands.createToast({
				type: 'success',
				children: t`Voice diagnostics uploaded!`,
			});
		} catch (error) {
			logger.error('Voice diagnostics upload failed', error);
			let message = t`Voice diagnostics upload failed.`;
			if (error instanceof VoiceDiagnosticsUploadError) {
				if (error.status === 401) {
					message = t`You must be signed in to upload voice diagnostics.`;
				} else if (error.status === 413) {
					message = t`Voice diagnostics archive is too large to upload.`;
				} else if (error.status === 429) {
					const retry = error.retryAfterSeconds;
					message = retry
						? t`Voice diagnostics uploads are rate-limited. Try again in ${retry} seconds.`
						: t`Voice diagnostics uploads are rate-limited. Try again later.`;
				} else if (error.status === 0) {
					message = t`Network error while uploading voice diagnostics.`;
				} else {
					message = error.message || message;
				}
			}
			ToastCommands.createToast({type: 'error', children: message});
		} finally {
			setIsUploading(false);
		}
	}, [t]);
	return (
		<Button onClick={() => void handleUpload()} submitting={isUploading} fitContainer={false}>
			{t`Upload Voice Diagnostics`}
		</Button>
	);
};
